import {type ChangeEvent} from 'react';

import {useThemeColors} from '@nfq/colors';

import {useFormField} from 'UI/components/action/inputs/hooks/useFormField';

import type {ValidationFunc} from 'Shared/formHelperTypes';

/**
 * The `useFormCheckboxInput` hook provides comprehensive functionality for managing checkbox input fields within forms.
 * This custom hook encapsulates all the necessary logic for handling checkbox state, validation, error management, and change events.
 * It integrates seamlessly with the form field system while providing theme-aware styling capabilities and proper event handling.
 *
 * @param name         The unique identifier name for the checkbox input field within the form.
 * @param initialValue The initial boolean value that determines whether the checkbox starts as checked or unchecked.
 * @param validation   An optional validation function that will be executed to validate the checkbox state, or undefined if no validation is required.
 * @param onChange     A callback function that handles additional custom logic when the checkbox value changes.
 * @returns An object containing the theme colors, error state, field reference, change handler, and current checkbox value.
 *
 * @example
 * ```tsx
 * const {colors, error, fieldRef, handleChange, value} = useFormCheckboxInput(
 *     'acceptTerms',
 *     false,
 *     (value) => value ? null : 'You must accept the terms',
 *     (e) => console.log('Checkbox changed:', e.target.checked)
 * );
 *
 * return (
 *     <input
 *         ref={fieldRef}
 *         type="checkbox"
 *         checked={value}
 *         onChange={handleChange}
 *     />
 * );
 * ```
 */
export const useFormCheckboxInput = (
    name: string,
    initialValue: boolean,
    validation: (ValidationFunc | undefined),
    onChange: (e: ChangeEvent<HTMLInputElement>) => void
) => {
    const colors = useThemeColors();
    const {error, fieldRef, handleFieldChange, value}
        = useFormField<boolean, HTMLInputElement>(name, initialValue, initialValue, false, validation);

    /**
     * Combines custom and built-in logic to handle changes to a checkbox input. This function is designed to facilitate
     * additional actions specified in the `onChange` callback while also integrating with the form's state management system
     * to update the checkbox's checked state. This ensures that all associated logic and state updates are handled consistently
     * and efficiently whenever the checkbox's state changes.
     *
     * @param e The change event object from the checkbox input, containing the new checked state.
     *
     * @example
     * ```ts
     * <input type="checkbox" onChange={handleChange} />
     * ```
     */
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        onChange(e);
        handleFieldChange(e.target.checked);
    };

    return {
        colors,
        error,
        fieldRef,
        handleChange,
        value
    };
};